/**
 * chatToolPreview — pure helpers that turn raw tool-call / tool-result JSON
 * into small human-readable preview strings for the chat's tool cards.
 * Cheap by design: one JSON.parse, a few property probes, graceful fallbacks.
 */

/** Tool families — drive the per-tool icon + tint on the preview cards. */
export type ToolFamily = 'schedule' | 'export' | 'quote' | 'feedback' | 'catalog'
  | 'query' | 'report' | 'photo' | 'chart' | 'help' | 'generic';

export interface ToolCallInfo {
  name: string;
  input: Record<string, unknown>;
  /** e.g. "days: 14 · person: Sarah" — first few args, short values only. */
  argSummary: string;
}

export interface DownloadInfo {
  url: string;
  filename: string;
  rowCount: number | null;
}

export interface ToolResultPreview {
  family: ToolFamily;
  /** One-line human summary, e.g. "Machine schedule — 14 bookings across 6 machines". */
  summary: string;
  /** Present when the result is a CSV export → render a Download card. */
  download: DownloadInfo | null;
}

/** "get_time_off" → "Get time off". */
export function prettifyToolName(name: string): string {
  const words = name.replace(/[_-]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : 'Tool';
}

export function toolFamily(name: string): ToolFamily {
  const n = name.toLowerCase();
  if (n === 'export_to_csv') return 'export';
  if (n.includes('quote')) return 'quote';
  if (n.includes('feedback')) return 'feedback';
  if (n.includes('catalog')) return 'catalog';
  if (n.includes('photo')) return 'photo';
  if (n.includes('report')) return 'report';
  if (n.includes('help')) return 'help';
  if (n.includes('reading') || n.includes('plot') || n.includes('chart')) return 'chart';
  if (n.includes('schedule') || n.includes('machine') || n.includes('capacity')
    || n.includes('assignment') || n.includes('skill') || n.includes('time_off')
    || n.includes('conflict') || n.includes('utilization')) return 'schedule';
  return 'query';
}

/** Parse a persisted assistant tool-call message's ToolInput column ({name, input}). */
export function parseToolCall(toolInputJson: string | null): ToolCallInfo {
  let name = 'tool';
  let input: Record<string, unknown> = {};
  try {
    const parsed = JSON.parse(toolInputJson || '{}');
    if (typeof parsed?.name === 'string' && parsed.name) name = parsed.name;
    if (parsed?.input && typeof parsed.input === 'object') input = parsed.input as Record<string, unknown>;
  } catch { /* keep fallbacks */ }

  const parts: string[] = [];
  for (const [k, v] of Object.entries(input)) {
    if (v === null || v === undefined || v === '') continue;
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    parts.push(`${k}: ${s.length > 28 ? `${s.slice(0, 25)}…` : s}`);
    if (parts.length >= 3) break;
  }
  return { name, input, argSummary: parts.join(' · ') };
}

// ── result summaries ────────────────────────────────────────────────────────

function firstArrayLength(obj: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = obj[k];
    if (Array.isArray(v)) return v.length;
  }
  return null;
}

function plural(n: number, word: string): string {
  if (n === 1) return `${n} ${word}`;
  return `${n} ${word.endsWith('y') ? `${word.slice(0, -1)}ies` : `${word}s`}`;
}

/** "entries" → "entry", "bookings" → "booking". */
function singular(word: string): string {
  if (word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
}

/**
 * Derive a one-line human summary (and optional download payload) for a tool
 * result. `toolName` may be '' when unknown — falls back to shape probing.
 */
export function summarizeToolResult(toolName: string, output: string): ToolResultPreview {
  const family = toolName ? toolFamily(toolName) : 'query';
  const fallback: ToolResultPreview = {
    family,
    summary: toolName ? `${prettifyToolName(toolName)} — result received` : 'Tool result',
    download: null,
  };

  let json: Record<string, unknown> | null = null;
  try {
    const parsed = JSON.parse(output);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) json = parsed as Record<string, unknown>;
    else if (Array.isArray(parsed)) return { ...fallback, summary: `${plural(parsed.length, 'row')} returned` };
  } catch {
    // Plain-text result (help topics etc.) — first line as the summary.
    const firstLine = output.split('\n').find((l) => l.trim());
    if (firstLine) return { ...fallback, summary: firstLine.trim().slice(0, 90) };
    return fallback;
  }
  if (!json) return fallback;

  if (typeof json.error === 'string' && json.error) {
    return { ...fallback, summary: `Error — ${json.error.slice(0, 80)}` };
  }

  // CSV export → Download card.
  if (typeof json.downloadUrl === 'string' && json.downloadUrl.includes('/exports/download')) {
    const rowCount = typeof json.rowCount === 'number' ? json.rowCount : null;
    const filename = typeof json.filename === 'string' ? json.filename : 'export.csv';
    return {
      family: 'export',
      summary: `CSV export ready — ${rowCount !== null ? plural(rowCount, 'row') : filename}`,
      download: { url: json.downloadUrl, filename, rowCount },
    };
  }

  // Chart payloads (handled full-width by the caller; still give a line).
  const chart = (json.chart as Record<string, unknown> | undefined)?.plotType || json.plotType;
  if (typeof chart === 'string') {
    const title = (json.chart as Record<string, unknown> | undefined)?.title || json.title;
    return { family: 'chart', summary: `Chart — ${typeof title === 'string' && title ? title : chart}`, download: null };
  }

  const name = toolName.toLowerCase();

  if (name === 'get_machine_schedule') {
    const bookings = firstArrayLength(json, ['bookings']);
    if (bookings !== null) {
      const machines = new Set(((json.bookings as Array<Record<string, unknown>>) || [])
        .map((b) => b.machineName ?? b.MachineName ?? b.machine)).size;
      return { family, summary: `Machine schedule — ${plural(bookings, 'booking')}${machines > 0 ? ` across ${plural(machines, 'machine')}` : ''}`, download: null };
    }
  }
  if (name === 'get_time_off') {
    const n = firstArrayLength(json, ['absences', 'timeOff', 'entries']);
    if (n !== null) return { family, summary: `Time off — ${plural(n, 'absence')} in range`, download: null };
  }
  if (name === 'get_schedule_conflicts') {
    const n = firstArrayLength(json, ['conflicts']);
    if (n !== null) return { family, summary: n === 0 ? 'No scheduling conflicts found' : `${plural(n, 'scheduling conflict')} found`, download: null };
  }
  if (name === 'get_quote_pipeline') {
    const n = firstArrayLength(json, ['quotes', 'pipeline', 'stages']);
    if (n !== null) return { family, summary: `Quote pipeline — ${plural(n, 'entry')}`, download: null };
  }
  if (name === 'get_feedback_tickets') {
    const n = firstArrayLength(json, ['tickets']);
    if (n !== null) return { family, summary: `Feedback — ${plural(n, 'ticket')}`, download: null };
  }
  if (name === 'get_catalog_health') {
    const unmapped = firstArrayLength(json, ['unmapped', 'unmappedTests']);
    if (unmapped !== null) return { family, summary: `Catalogue health — ${plural(unmapped, 'unmapped test')}`, download: null };
  }
  if (name === 'query_database' && typeof json.rowCount === 'number') {
    return { family, summary: `Query — ${plural(json.rowCount, 'row')}${typeof json.returned === 'number' && json.returned < json.rowCount ? ` (${json.returned} shown)` : ''}`, download: null };
  }
  if (name === 'list_photos') {
    const n = firstArrayLength(json, ['photos']);
    if (n !== null) return { family, summary: `${plural(n, 'photo')} found`, download: null };
  }
  if (name === 'get_report_status') {
    const type = json.reportType ?? json.report;
    return { family, summary: `Report status${typeof type === 'string' ? ` — ${type}` : ''}`, download: null };
  }
  if (name === 'get_test_data' || name === 'get_test_statistics' || name === 'get_test_readings') {
    const type = json.testType ?? json.TestName ?? json.testName;
    if (typeof type === 'string' && type) return { family, summary: `${prettifyToolName(name)} — ${type}`, download: null };
  }

  // Generic shape probing: report the first array's size.
  for (const [k, v] of Object.entries(json)) {
    if (Array.isArray(v)) return { ...fallback, summary: `${prettifyToolName(toolName || 'result')} — ${plural(v.length, singular(k))}` };
  }
  return fallback;
}
